// §9.3 / §9.4 — the bridge host: one `message` listener for every app frame on the desktop.
//
// Three rules, all of them from §9.3, and all of them about what happens when something is wrong:
//
//  1. **Every inbound message is checked** — the event's origin against the manifest's, and the
//     envelope's token against the one minted for *that window instance*.
//  2. **A failure is dropped and counted, never thrown.** An exception on the message path is a
//     way to stop the shell listening, and a killed frame is a way to lose a user's work. The drop
//     log is what App Inspector renders (§11.12).
//  3. **A silent app is left alone.** Three seconds after the frame loads, an app that has not said
//     hello is marked unbridged — and keeps running. Plenty of useful things are just a web page.
import {
  HANDSHAKE_TIMEOUT_MS,
  decodeMessage,
  envelope as buildEnvelope,
  type AppToShellType,
  type DecodedMessage,
  type ShellToAppType,
  type UserIdentity,
} from '@hangar/shared';
import { originMatches } from './origin';
import { createTokenMinter, type RandomBytes, type TokenMinter } from '../tokens';

export type BridgeState = 'pending' | 'bridged' | 'unbridged';

export type DropReason =
  /** No open connection owns the window this came from, and no token identifies one. */
  | 'unknown-source'
  /** The right window, the wrong origin — or an opaque one (§9.4). */
  | 'origin'
  /** The right origin, a token that is not this instance's. */
  | 'token'
  /** Not a §9.3 envelope at all. */
  | 'envelope'
  /** A well-formed envelope naming a message app→shell does not carry. */
  | 'unknown-message'
  /** The right message, a payload that is not its shape. */
  | 'payload'
  /** A real message from an app that has not completed the handshake. */
  | 'not-bridged';

export interface BridgeDrop {
  at: number;
  /** Null when the frame could not even be attributed to a connection. */
  instanceKey: string | null;
  appId: string | null;
  reason: DropReason;
  detail: string;
}

export interface BridgeLogEntry {
  at: number;
  instanceKey: string;
  appId: string;
  direction: 'in' | 'out';
  key: string;
  id: string;
  /** §11.12 — approximate payload bytes, for the Inspector's log (D-132). */
  size: number;
}

/**
 * An approximate on-the-wire size for one payload.
 *
 * **Typed arrays are counted as bytes, never stringified.** `fs.read` of a 10 MB file answers with
 * a `Uint8Array`, and `JSON.stringify` would turn it into a 40 MB string of `{"0":1,"1":2,…}` —
 * to produce a number nobody needed that precisely. Everything else is JSON, which is what the
 * structured clone will roughly cost anyway.
 */
export function frameSize(payload: unknown): number {
  if (payload === null || payload === undefined) return 0;
  if (typeof payload === 'string') return payload.length;
  if (ArrayBuffer.isView(payload)) return payload.byteLength;
  if (typeof payload !== 'object') return String(payload).length;

  let total = 2;
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    total += key.length + 3;
    total += ArrayBuffer.isView(value) ? value.byteLength : jsonSize(value);
  }
  return total;
}

function jsonSize(value: unknown): number {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    // Circular, or a `BigInt`. It could not have crossed a `postMessage` either.
    return 0;
  }
}

/** The narrow slice of an iframe's `contentWindow` the host needs. */
export interface FramePoster {
  postMessage(message: unknown, targetOrigin: string): void;
}

export interface BridgeMessageEvent {
  origin: string;
  source: unknown;
  data: unknown;
}

export interface ConnectOptions {
  instanceKey: string;
  appId: string;
  /** The manifest's origin. Every inbound message is compared against this and nothing else. */
  origin: string;
  /** What `handshake/welcome` tells the app about the desktop it is living in. */
  welcome: () => {
    skin: string;
    theme: 'light' | 'dark';
    locale: string;
    user: UserIdentity;
    reservedChords: string[];
    permissions: string[];
  };
}

export interface BridgeConnection {
  readonly instanceKey: string;
  readonly appId: string;
  readonly origin: string;
  readonly token: string;
  readonly state: BridgeState;
  /** The frame, once it exists. Set by the host layer when the iframe is attached. */
  setFrame(frame: FramePoster | null): void;
  /** Send a shell→app message. Returns false when there is nowhere to send it. */
  send(key: ShellToAppType, payload: unknown, id?: string): boolean;
  /** Send and await the app's reply to that message id. Rejects on timeout. */
  request(key: ShellToAppType, payload: unknown, timeoutMs?: number): Promise<DecodedMessage>;
  drops: number;
}

export interface BridgeHostOptions {
  /** Routed here once a message has passed origin, token, shape and handshake. */
  onMessage: (connection: BridgeConnection, message: DecodedMessage) => void;
  /** Called when a connection's state changes, for App Inspector and the health model. */
  onState?: (connection: BridgeConnection, state: BridgeState) => void;
  onDrop?: (drop: BridgeDrop) => void;
  now?: () => number;
  randomBytes?: RandomBytes;
  handshakeTimeoutMs?: number;
  /** How many log entries and drops to keep. The Inspector shows a window, not a history. */
  logLimit?: number;
}

export interface BridgeHost {
  connect(options: ConnectOptions): BridgeConnection;
  disconnect(instanceKey: string): void;
  get(instanceKey: string): BridgeConnection | undefined;
  /** Every open connection, for App Inspector. */
  connections(): BridgeConnection[];
  /** Handle one `message` event. Never throws. */
  handleMessage(event: BridgeMessageEvent): void;
  attach(target: {
    addEventListener(type: string, listener: (event: Event) => void): void;
    removeEventListener(type: string, listener: (event: Event) => void): void;
  }): () => void;
  log(): readonly BridgeLogEntry[];
  drops(): readonly BridgeDrop[];
  readonly tokens: TokenMinter;
}

interface Live extends BridgeConnection {
  frame: FramePoster | null;
  /** What to tell this app about the desktop when it says hello. */
  welcome: ConnectOptions['welcome'];
  timer: ReturnType<typeof setTimeout> | null;
  /** Message ids this connection is waiting for a reply to. */
  pending: Map<string, { resolve: (m: DecodedMessage) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>;
  mutableState: BridgeState;
}

const DEFAULT_LOG_LIMIT = 200;

export function createBridgeHost(options: BridgeHostOptions): BridgeHost {
  const now = options.now ?? (() => Date.now());
  const handshakeMs = options.handshakeTimeoutMs ?? HANDSHAKE_TIMEOUT_MS;
  const logLimit = options.logLimit ?? DEFAULT_LOG_LIMIT;
  const tokens = createTokenMinter(options.randomBytes);

  const byKey = new Map<string, Live>();
  const byToken = new Map<string, Live>();
  const log: BridgeLogEntry[] = [];
  const drops: BridgeDrop[] = [];
  let nextId = 0;

  function remember<T>(list: T[], entry: T): void {
    list.push(entry);
    if (list.length > logLimit) list.splice(0, list.length - logLimit);
  }

  function drop(
    connection: Live | null,
    reason: DropReason,
    detail: string,
  ): void {
    if (connection) connection.drops += 1;
    const entry: BridgeDrop = {
      at: now(),
      instanceKey: connection?.instanceKey ?? null,
      appId: connection?.appId ?? null,
      reason,
      detail,
    };
    remember(drops, entry);
    options.onDrop?.(entry);
  }

  function setState(connection: Live, state: BridgeState): void {
    if (connection.mutableState === state) return;
    connection.mutableState = state;
    options.onState?.(connection, state);
  }

  /** Which connection a message event belongs to: the window it came from, else its token. */
  function attribute(event: BridgeMessageEvent): Live | null {
    for (const connection of byKey.values()) {
      if (connection.frame !== null && event.source === connection.frame) return connection;
    }
    const token = (event.data as { token?: unknown } | null)?.token;
    return typeof token === 'string' ? (byToken.get(token) ?? null) : null;
  }

  const host: BridgeHost = {
    tokens,

    connect(connectOptions) {
      const existing = byKey.get(connectOptions.instanceKey);
      if (existing) return existing;

      const token = tokens.mint();
      const connection: Live = {
        instanceKey: connectOptions.instanceKey,
        appId: connectOptions.appId,
        origin: connectOptions.origin,
        token,
        welcome: connectOptions.welcome,
        frame: null,
        timer: null,
        pending: new Map(),
        mutableState: 'pending',
        drops: 0,

        get state() {
          return connection.mutableState;
        },

        setFrame(frame) {
          connection.frame = frame;
        },

        send(key, payload, id) {
          if (!connection.frame) return false;
          const messageId = id ?? `s${(nextId += 1)}`;
          // **Never `'*'`.** A wildcard target origin would post the capability token to whatever
          // happens to be in the frame, which is the one thing §9.3's token exists to prevent.
          connection.frame.postMessage(
            buildEnvelope(messageId, key, payload, connection.token),
            connection.origin,
          );
          remember(log, {
            at: now(),
            instanceKey: connection.instanceKey,
            appId: connection.appId,
            direction: 'out',
            key,
            id: messageId,
            size: frameSize(payload),
          });
          return true;
        },

        async request(key, payload, timeoutMs = 5_000) {
          const messageId = `s${(nextId += 1)}`;
          if (!connection.send(key, payload, messageId)) {
            throw new Error(`${connection.appId} has no frame to send '${key}' to`);
          }
          return new Promise<DecodedMessage>((resolve, reject) => {
            const timer = setTimeout(() => {
              connection.pending.delete(messageId);
              reject(new Error(`${connection.appId} did not answer '${key}' within ${timeoutMs}ms`));
            }, timeoutMs);
            connection.pending.set(messageId, { resolve, reject, timer });
          });
        },
      };

      byKey.set(connection.instanceKey, connection);
      byToken.set(token, connection);

      // F7 — three seconds, then the app is left to be a web page.
      connection.timer = setTimeout(() => {
        connection.timer = null;
        if (connection.mutableState === 'pending') setState(connection, 'unbridged');
      }, handshakeMs);

      return connection;
    },

    disconnect(instanceKey) {
      const connection = byKey.get(instanceKey);
      if (!connection) return;
      if (connection.timer !== null) clearTimeout(connection.timer);
      for (const waiter of connection.pending.values()) {
        clearTimeout(waiter.timer);
        waiter.reject(new Error('the window closed'));
      }
      connection.pending.clear();
      byKey.delete(instanceKey);
      byToken.delete(connection.token);
      // §9.3 — a token is never reused, and it dies with the window that held it.
      tokens.retire(connection.token);
    },

    get: (instanceKey) => byKey.get(instanceKey),
    connections: () => [...byKey.values()],
    log: () => log,
    drops: () => drops,

    handleMessage(event) {
      const connection = attribute(event);
      if (!connection) {
        drop(null, 'unknown-source', `no open connection owns a frame at '${event.origin}'`);
        return;
      }

      if (!originMatches(event.origin, connection.origin)) {
        drop(
          connection,
          'origin',
          `expected '${connection.origin}', got '${String(event.origin)}'`,
        );
        return;
      }

      const decoded = decodeMessage(event.data, 'appToShell');
      if (!decoded.ok) {
        drop(connection, decoded.reason, decoded.detail);
        return;
      }

      // **`handshake/hello` is exempt from the token check, and must be.** The token is minted by
      // the shell and reaches the app in the *welcome* — an app cannot know it before it has said
      // hello. What authenticates a hello is the pair §9.4 actually relies on: it came from this
      // window, and it came from the declared origin. Everything after it carries the token
      // (D-113).
      if (decoded.key !== 'handshake/hello' && decoded.envelope.token !== connection.token) {
        // Deliberately says nothing about *which* token would have worked.
        drop(connection, 'token', `token does not match the one minted for this window`);
        return;
      }

      remember(log, {
        at: now(),
        instanceKey: connection.instanceKey,
        appId: connection.appId,
        direction: 'in',
        key: decoded.key,
        id: decoded.envelope.id,
        size: frameSize(decoded.payload),
      });

      if (decoded.key === 'handshake/hello') {
        // A late hello is honoured. F7 says a silent app *runs unbridged*, not that it is barred
        // for the rest of its life — and an app that woke up slowly loses nothing by being heard.
        if (connection.timer !== null) {
          clearTimeout(connection.timer);
          connection.timer = null;
        }
        connection.send('handshake/welcome', {
          token: connection.token,
          ...connection.welcome(),
        });
        setState(connection, 'bridged');
        options.onMessage(connection, decoded);
        return;
      }

      if (connection.mutableState !== 'bridged') {
        drop(connection, 'not-bridged', `'${decoded.key}' before the handshake`);
        return;
      }

      // A reply to something the shell asked. `to` is on every reply payload in §9.3's table.
      const to = (decoded.payload as { to?: unknown } | null)?.to;
      if (typeof to === 'string') {
        const waiter = connection.pending.get(to);
        if (waiter) {
          clearTimeout(waiter.timer);
          connection.pending.delete(to);
          waiter.resolve(decoded);
        }
      }

      options.onMessage(connection, decoded);
    },

    attach(target) {
      const listener = (event: Event): void => {
        const message = event as unknown as BridgeMessageEvent;
        host.handleMessage(message);
      };
      target.addEventListener('message', listener);
      return () => target.removeEventListener('message', listener);
    },
  };

  return host;
}

/** The app→shell keys, for anything that wants to enumerate them (App Inspector, tests). */
export type InboundKey = AppToShellType;
